import * as BABYLON from 'babylonjs';
import _ from 'lodash';
/* eslint-disable new-cap */

/**
 * De-populate a single property of a given object. The property is assumed to be an array which may contain null entries.
 *
 * @param {*} data - object to de-populate
 * @param {string} prop - name of the property
 */
const dePopulateProperty = (data, prop) => {
  if (data[prop] && data[prop].length) {
    for (let i = 0; i < data[prop].length; ++i) {
      // We only need to de-populate if it's a valid entry, otherwise we do nothing.
      if (data[prop][i]) {
        console.assert(data[prop][i]._id);
        data[prop][i] = data[prop][i]._id;
      }
    }
  } else {
    data[prop] = [];
  }
};

/**
 * De-populate a single, object-like property of a given object, only keeping the "_id" field.
 *
 * If there is no valid "_id", or the property is not an Object (or it does not exists) nothing is done.
 *
 * @param {*} data - object to de-populate
 * @param {string} prop - name of the property
 */
const dePopulateObjectProperty = (data, prop) => {
  if (data[prop] && _.isObject(data[prop]) && data[prop]._id) {
    data[prop] = data[prop]._id;
  }
};

/**
 * De-populate the populated properties of a product before saving/updating it in place.
 *
 * Currently we de-populate the following fields: "availableGrippers", "grips", "states", "device".
 * Note that some of these may be null.
 * @param {object} data - product to do the de-populate operation on
 */
const dePopulate = (data) => {
  dePopulateProperty(data, 'availableGrippers');
  dePopulateProperty(data, 'grips');
  dePopulateProperty(data, 'states');
  dePopulateObjectProperty(data, 'device');
};

/**
 * Gets the scalefactor necessary to convert the units used by the current model to meters.
 *
 * @param {object} product  - current product (used to get the unit)
 * @return {number} - scalefactor to be used to convert to meters
 */
const getScaleFactor = (product) => {
  let scalefactor = 1;
  switch (product.unit) {
    case 'mm':
      scalefactor = 1000;
      break;
    case 'cm':
      scalefactor = 100;
      break;
    case 'm':
      scalefactor = 1;
      break;
    default:
      console.warn('Unknown unit: %s, assuming meters.', product.unit);
  }
  return scalefactor;
};

/**
 * Gets the scalefactor necessary to convert the units used by the current model to millimeters.
 *
 * Note that it's simpler to have two separate functions for this because they are assymetric
 * due to the ratios between mm/cm/m.
 *
 * @param {object} product  - current product (used to get the unit)
 * @return {number} - scalefactor to be used to convert to millimeters
 */
const getMMScaleFactor = (product) => {
  let scalefactor = 1000;
  switch (product.unit) {
    case 'mm':
      scalefactor = 1;
      break;
    case 'cm':
      scalefactor = 10;
      break;
    case 'm':
      scalefactor = 1000;
      break;
    default:
      console.warn('Unknown unit: %s, assuming meters.', product.unit);
  }
  return scalefactor;
};

/**
 * Calculate the transformation that puts the object from the given stable state into the world
 *
 * @param {object} product  - current product
 * @param {number} stateIndex - index of the currently used stable state (used to look up properties from the product)
 * @return {BABYLON.Matrix} - calculated transform as a BabylonJS 4x4 Matrix
*/

/**
 * Calculate the transformation that puts the object from the given stable state into the world
 *
 * @param {object} state - The current state
 * @return {BABYLON.Matrix} - calculated transform as a BabylonJS 4x4 Matrix
*/
const calculateObject2Plane = (state) => {
  const state2planeMat = BABYLON.Matrix.FromArray(
      [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, state.meanSize[2] / 2.0, 1]);
  // BabylonJS is row-major so we need to transpose because our arrays are stored column-major
  // (although for the above matrix this is actually a no-op)
  state2planeMat.transpose();
  console.log('state frame: ', state.frame);
  const frame = BABYLON.Matrix.FromArray(state.frame);
  console.log('frame matrix: ', frame.m);
  // Again, we need to transpose because we store everything column-major - this is not always a no-op!
  frame.transpose();
  // plane2obj -> obj2plane
  frame.invert();
  // BabylonJS keeps the transition in the bottom row so we need to post-multiply!
  const stateTrafo = frame.multiply(state2planeMat);

  return stateTrafo;
};

/**
 * Calculate the grip frame matrix based on the two parameters of the grip (distance, rotation)
 *
 * @param {number} translation - grip translation along the symmetry axis
 * @param {number} offsetX - grip translation along the X axis
 * @param {number} offsetY - grip translation along the Y axis
 * @param {number} rotZ - grip rotation along the symmetry axis
 * @param {number} rotY - grip rotation along the symmetry axis
 * @param {number} rotX - grip rotation along the symmetry axis
 * @return {BABYLON.Matrix} - calculated grip frame matrix (F_gw)
 */
const calculateGrip2Plane = (translation, offsetX, offsetY, rotZ, rotY, rotX) => {
  let rotationMatrixZ = BABYLON.Matrix.RotationZ(rotZ);
  rotationMatrixZ = rotationMatrixZ.transpose();
  let rotationMatrixY = BABYLON.Matrix.RotationY(rotY);
  rotationMatrixY = rotationMatrixY.transpose();
  let rotationMatrixX = BABYLON.Matrix.RotationX(rotX);
  rotationMatrixX = rotationMatrixX.transpose();

  let rotationMatrix = rotationMatrixZ.multiply(rotationMatrixY);
  rotationMatrix = rotationMatrix.multiply(rotationMatrixX);
  const translationMatrix = rotationMatrix.multiply(
      BABYLON.Matrix.Translation(offsetX, offsetY, translation),
  );

  return translationMatrix;
};

/**
 * Calculate the grip transformation that we need to store in the backend.
 *
 * See https://gitlab.vathos.net/production/vue.ui.picking.vathos.net/-/issues/65#note_13406 for details.
 *
 * @param {object} product  - current product
 * @param {{rotation: number, translation: number}} grip - grip state defined by the two parameters
 * @param {object} state - Currently used stable state (used to look up properties from the product)
 * @return {Array.<number>} - A 16 elem array representing the final grip transform in a row-major order
 */
const calculateGripTransform = (product, grip, state) => {
  const grip2plane = calculateGrip2Plane(grip.translation, grip.offsetX, grip.offsetY, grip.rotZ, grip.rotY, grip.rotX);
  grip2plane.transpose();
  console.log('calculateGripTransform -> gripFrame: ', grip2plane.m);
  const object2plane = calculateObject2Plane(state);
  console.log('calculateGripTransform -> object2plane: ', object2plane.m);
  object2plane.invert(); // This actually becomes plane2object once inverted!
  const result = grip2plane.multiply(object2plane); // This should be the correct multiplication order!
  console.log('calculateGripTransform -> grip result: ', result.m);
  // We need to transpose to a column-major format as that's what we store in the backend!
  result.transpose();
  return Array.from(result.m);
};

/**
 * Get the grip parameters from the transformation stored in the backend.
 *
 *
 * @param {object} grip  - the grip from the backend
 * @param {object} state - State from the backend
 * @return {{rotation: number, translation: number}} - grip state (re)calculated from the stored grip frame.
 */
const getGripParameters = (grip, state) => {
  console.log('OUTPUT TCL -> file: products.helper.js:185 -> getGripParameters -> state:', state);
  // Create a BabylonJS matrix from the grip's frame (stored in column-major!)
  const storedGripFrame = BABYLON.Matrix.FromArray(grip.frame);
  console.log('OUTPUT TCL -> file: products.helper.js:189 -> getGripParameters -> grip.frame:', grip.frame);
  // transpose to make it row-major as BabylonJS uses that
  storedGripFrame.transpose();
  console.log('reloaded grip: ', storedGripFrame.m);
  // multiply by the state transform
  const object2plane = calculateObject2Plane(state);
  console.log('object2plane: ', object2plane.m);
  const result = storedGripFrame.multiply(object2plane);
  console.log('reconstructed grip: ', result.m);
  let rotY = Math.asin(-result.m[8]);
  let rotX = Math.atan2(result.m[9], result.m[10]);
  let rotZ = Math.atan2(result.m[4], result.m[0]);
  if (rotX < 0) {
    rotX = 2 * Math.PI + rotX;
  }
  if (rotY < 0) {
    rotY = 2 * Math.PI + rotY;
  }
  if (rotZ < 0) {
    rotZ = 2 * Math.PI + rotZ;
  }
  console.log('reconstructed angles along x, y, z: ', rotX, rotY, rotZ);
  console.log('reconstructed translation: ', result.m[14]);
  return {
    rotX: rotX,
    rotY: rotY,
    rotZ: rotZ,
    translation: result.m[14],
    offsetX: result.m[12],
    offsetY: result.m[13],
  };
};


/**
 * Based on the grip state, create a valid grip document (including frame) that can be persisted by the backend.
 *
 * @param {object} product - current product (used to get current state)
 * @param {{rotation: number, translation: number}} grip - grip state defined by the two parameters
 * @param {object} state - Currently used stable state
 * @return {object} valid grip document.
 */
const state2grip = (product, grip, state) => {
  const newGrip = {
    gripper: product.availableGrippers[0],
    typeOfFrame: 'kuka',
    accepted: false,
    frame: calculateGripTransform(product, grip, state),
  };
  return newGrip;
};

/**
 * Calculate the valid grip orientation range for the given product based on the heading angle of the state.
 *
 * @param {object} product - current product
 * @return {{min: number, max: number}} grip orientation range calculated from the productstate
 */
const calculateGripOrientationRange = (product) => {
  // Note that we assume that the heading angle is the same for all states (which should be the case).
  const headingAngle = product.states[0].headingAngle;
  const range = {
    min: 0,
    max: 360,
  };
  if (headingAngle > 0) {
    // Convert to degrees and round to closest integer
    range.max = Math.round(headingAngle * 180 / Math.PI);
  }
  return range;
};

/**
 *  Check if the product has states and and how many of them are not null
 *
 * @param  {object} product - current product
 * @return  {integer} - number of states that are not null
 */
const getNumberOfStates = (product)=>{
  let numberOfStates = 0;
  if (!product.states?.length) return numberOfStates;
  for (const state of product.states) {
    if (!state) continue;
    numberOfStates++;
  }
  return numberOfStates;
};

/**
 *  Check if the product has grips and and how many of them are not null
 *
 * @param  {object} product - current product
 * @return  {integer} - number of grips that are not null
 */
const getNumberOfGrips = (product)=>{
  let numberOfGrips = 0;
  if (!product.grips?.length) return numberOfGrips;
  for (const grip of product.grips) {
    if (!grip) continue;
    numberOfGrips++;
  }
  return numberOfGrips;
};

/**
 * Calculates the euler angles
 *
 * @param  {object} grip2plane - current grip2plane
 * @return {{rotX: number, rotY: number, rotZ: number}}
 */
const calculateEulerAngles = (grip2plane)=>{
  let rotY = Math.asin(-grip2plane.m[8]);
  let rotX = Math.atan2(grip2plane.m[9], grip2plane.m[10]);
  let rotZ = Math.atan2(grip2plane.m[4], grip2plane.m[0]);

  rotX = rotX % (2 * Math.PI);
  rotY = rotY % (2 * Math.PI);
  rotZ = rotZ % (2 * Math.PI);

  return {rotX: rotX, rotY: rotY, rotZ: rotZ};
};

export default {
  dePopulateProperty,
  dePopulate,
  dePopulateObjectProperty,
  state2grip,
  getScaleFactor,
  getMMScaleFactor,
  calculateObject2Plane,
  calculateGripOrientationRange,
  getNumberOfStates,
  getNumberOfGrips,
  getGripParameters,
  calculateGrip2Plane,
  calculateEulerAngles,
};
